import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, interval, of } from 'rxjs';
import { catchError, concatMap, delay, exhaustMap, map, switchMap, takeUntil, withLatestFrom, debounceTime, mergeMap } from 'rxjs/operators';
import { ReservationApiService } from 'src/app/apis/reservation-api.service';
import { CapacitorStorageService } from 'src/app/lib/capacitor-storage.service';
import { Common } from 'src/app/lib/common-methods';
import { LocalStorageService } from 'src/app/lib/local-storage.service';
import { ReservationService } from 'src/app/lib/reservation.service';
import { environment } from 'src/environments/environment';
import * as AppActions from '../actions/app.actions';
import * as ReservationListActions from '../actions/reservation-list.actions';
import * as ReservationActions from '../actions/reservation.actions';
import * as AppSelectors from '../selectors/app.selectors';
import * as ReservationListSelectors from '../selectors/reservation-list.selectors';
import * as ReservationSelectors from '../selectors/reservation.selectors';
import { UtilsService } from 'src/app/lib/utils.service';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';

@Injectable()
export class ReservationListEffects { 
  loadReservations$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(
        ReservationListActions.loadReservations,
        ReservationListActions.setDate,
        ReservationListActions.setEditingReservationId,
        ReservationListActions.autoLoadReservations,
        TablesPlanActions.saveSettingsSuccess,
      ),
      withLatestFrom(this.store.select(ReservationListSelectors.selectAllReservations), this.store.select(ReservationListSelectors.selectDate)),
      mergeMap(([action, allReservations, date]) => {

        if (!this.localStorageService.isAuthenticated()) {
          this.router.navigate(["/auth/login"]);
          return EMPTY;
        }

        if ((action.type === ReservationListActions.setEditingReservationId.type)
          && allReservations?.length > 0) {
          return EMPTY;
        }

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return EMPTY;
        }

        // const showLoader = action.type !== ReservationListActions.autoLoadReservations.type;
        // if (showLoader) {
        //   this.showLoader();
        // }

        return this.reservationApiService.getReservations(parseInt(restaurantId), date)
          .pipe(
            map(data => {
              // this.hideLoader();
              const reservations = this.reservationService.initReservationList(Common.initialSortReservations(data.reservationsList));
              return ReservationListActions.loadReservationsSuccess({ reservations, shifts: data.shifts });
            }),
            catchError(error => {
              // this.hideLoader();
              return of(ReservationListActions.loadReservationsFailure({ error }));
            }))
      }
      )
    );
  });

  saveWorkingDateToLocalStorage$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationListActions.setDate),
      withLatestFrom(this.store.select(ReservationListSelectors.selectDate)),
      switchMap(([action, date]) => {

        this.localStorageService.saveWorkingDate(moment(date).format('yyyy-MM-DD'));

        return of({}).pipe(
          map(data => {
            return ReservationListActions.saveWorkingDateToLocalStorageSuccess();
          }))
      }
      )
    );
  });

  loadReservationsSuccess$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationListActions.loadReservationsSuccess),
      withLatestFrom(this.store.select(ReservationListSelectors.selectShifts), this.store.select(ReservationListSelectors.selectCachedReservationsList)),
      switchMap(([action, shifts, cachedReservationsList]) => {

        this.capacitorStorageService.setCachedReservationLists(cachedReservationsList);

        this.store.dispatch({ type: AppActions.checkForAdvertisment.type });
        
        if (shifts == null || shifts.length <= 0) {
          return EMPTY;
        }

        const processedShifts = this.reservationService.processShifts(JSON.parse(JSON.stringify(shifts)), true);

        return of({}).pipe(
          map(data => {
            return ReservationListActions.setProcessedShifts({ processedShifts });
          }))
      }
      )
    );
  });

  savePendingReservationsToLocalStorage$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationListActions.addPendingReservation),
      withLatestFrom(this.store.select(ReservationListSelectors.selectPendingReservations)),
      switchMap(([action, pendingReservations]) => {

        if (pendingReservations == null || pendingReservations.length <= 0) {
          return EMPTY;
        }

        this.localStorageService.setPendingReservations(pendingReservations);

        return of({}).pipe(
          map(data => {
            return ReservationListActions.addPendingReservationSuccess();
          }))
      }
      )
    );
  });

  submitPendingReservation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.setIsOffline, ReservationListActions.submitPendingReservation, ReservationListActions.addPendingReservation, ReservationListActions.setPendingReservations),
      withLatestFrom(this.store.select(AppSelectors.selectIsOffline), this.store.select(ReservationListSelectors.selectPendingReservations)),
      concatMap(([action, isOffline, pendingReservations]) => {

        if (isOffline === true || pendingReservations == null || pendingReservations.length <= 0) {
          return EMPTY;
        }

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return EMPTY;
        }

        const reservation = JSON.parse(JSON.stringify(pendingReservations[0])); // make object mutable
        this.reservationService.prepareForSave(reservation);

        return this.reservationApiService.createOrUpdateReservation(parseInt(restaurantId), reservation)
          .pipe(
            map(data => {
              if (data?.ErrorCode != null && data?.ErrorCode.length > 0) {
                return ReservationActions.submitReservationFailure(data?.ErrorCode);
              }
              return ReservationListActions.submitPendingReservationSuccess({ reservation: pendingReservations[0] });
            }),
            catchError(error => of(ReservationListActions.submitPendingReservationFailure({ error }))))
      }
      )
    );
  });

  submitPendingReservationSuccess$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationListActions.submitPendingReservationSuccess),
      withLatestFrom(this.store.select(ReservationListSelectors.selectPendingReservations)),
      switchMap(([action, pendingReservations]) => {

        this.localStorageService.setPendingReservations(pendingReservations);

        this.store.dispatch({ type: ReservationListActions.loadReservations.type });

        return of({}).pipe(
          delay(5000),
          map(data => {
            return ReservationListActions.submitPendingReservation();
          }))
      }
      )
    );
  });

  updateListReservation$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationListActions.addPendingReservation, ReservationActions.submitReservationSuccess),
      withLatestFrom(this.store.select(ReservationSelectors.selectReservation)),
      switchMap(([action, reservation]) => {

        if (reservation == null) {
          return EMPTY;
        }

        return of({}).pipe(
          map(data => {
            return ReservationListActions.updateReservation({ reservation });
          }))
      }
      )
    );
  });

  refreshReservationsListOnInterval$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.setMenus),
      switchMap((action, num) => {
        return interval(this.localStorageService.getRefreshInterval()).pipe(
          map(data => {  
            if (this.utilService.getChangeRestaurantRequested() === true) return { type: 'CANCELLED' };

            return ReservationListActions.autoLoadReservations();
          })
        )
      })
    );
  });

  saveCompactListViewToLocalStorage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReservationListActions.setCompactView),
      switchMap((action, num) => {
        
        this.localStorageService.saveCompactListView(action.value);
        
        return of({}).pipe(
          map(data => {
            return ReservationListActions.setCompactViewSuccess();
          }))
      })
    );
  });

  saveListFiltersAndSortingToStorage$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(
        ReservationListActions.setFilters, 
        ReservationListActions.setSortType, 
        ReservationListActions.setSortDesc,
        ReservationListActions.setSelectedShift,
      ),
      withLatestFrom(
        this.store.select(ReservationListSelectors.selectFilters), 
        this.store.select(ReservationListSelectors.selectSortType),
        this.store.select(ReservationListSelectors.selectSortDesc),
        this.store.select(ReservationListSelectors.selectSelectedShift),
      ),
      switchMap(([action, filters, sortType, sortDesc, selectedShift]) => {

        this.capacitorStorageService.setListFiltersAndSorting({
          filters,
          sortType,
          sortDesc,
          selectedShift,
        });

        return of({}).pipe(
          map(data => {
            return ReservationListActions.saveListFiltersAndSortingToStorageSuccess();
          }))
      }
      )
    );
  });

  constructor(
    private actions$: Actions,
    private reservationApiService: ReservationApiService,
    private localStorageService: LocalStorageService,
    private capacitorStorageService: CapacitorStorageService,
    private reservationService: ReservationService,
    private router: Router,
    private utilService: UtilsService,
    private store: Store<{ date: Date }>,
    private spinner: NgxSpinnerService,
  ) { }

  showLoader() {
    this.hideLoader();

    this.loadingSpinnerTimeoutId = setTimeout(() => {
      this.spinner.show();
    }, environment.requestOverlayThreashold);
  }

  hideLoader() {
    if (this.loadingSpinnerTimeoutId != null) {
      clearTimeout(this.loadingSpinnerTimeoutId);
    }

    this.spinner.hide();
  }

  loadingSpinnerTimeoutId: null | ReturnType<typeof setTimeout> = null;
}
